<template>
  <div class="toolbar-wrapper" :class="{ 'collapsed': isCollapsed }">
    <!-- 顶部按钮栏 -->
    <div class="toolbar-header">
      <div class="toolbar-buttons" v-show="!isCollapsed">
        <button class="toolbar-btn" :class="{ 'active': videoType === 'text-to-video' }" @click="videoType = 'text-to-video'">文生视频</button>
        <button class="toolbar-btn" :class="{ 'active': videoType === 'image-to-video' }" @click="videoType = 'image-to-video'">图生视频</button>
        <button class="toolbar-btn" :class="{ 'active': videoType === 'digital-human' }" @click="videoType = 'digital-human'">数字人</button>
      </div>
      <!-- 收缩/展开按钮 -->
      <button class="collapse-btn" @click="toggleCollapse" :title="isCollapsed ? '展开工具栏' : '收缩工具栏'" :class="{ 'expanded': !isCollapsed }">
        <div class="collapse-arrow" :class="{ 'up': isCollapsed }">▼</div>
      </button>
    </div>
    
    <!-- 提示词输入区 -->
    <div class="prompt-input-container">
      <textarea 
        v-model="form.prompt" 
        class="prompt-input" 
        :placeholder="getPromptPlaceholder()"
        rows="3"
      ></textarea>
    </div>
    
    <!-- 底部参数选项 -->
    <div class="toolbar-options">
      <!-- 模型选择 -->
      <div class="option-group">
        <select v-model="form.model" class="option-select">
          <option value="" disabled>选择模型</option>
          <optgroup label="免费模型">
            <option v-for="id in freeModels" :key="id" :value="id">{{ models[id]?.name }}</option>
          </optgroup>
          <optgroup label="付费模型">
            <option v-for="id in paidModels" :key="id" :value="id">{{ models[id]?.name }}</option>
          </optgroup>
        </select>
      </div>
      
      <!-- 视频尺寸选择 -->
      <div class="option-group">
        <select v-model="form.size" class="option-select">
          <option v-for="size in videoSizes" :key="size.name" :value="size.name">{{ size.name }}</option>
        </select>
      </div>
      
      <!-- 时长选择 -->
      <div class="option-group">
        <select v-model="form.duration" class="option-select">
          <option v-for="dur in videoDurations" :key="dur.duration" :value="dur.duration">{{ dur.name }}</option>
        </select>
      </div>
      
      <!-- 上传按钮 -->
      <div class="upload-buttons">
        <input type="file" ref="imageUpload" accept="image/*" style="display: none" @change="handleImageUpload">
        <button 
          v-if="(videoType === 'image-to-video' || videoType === 'digital-human') && !uploadedImagePreview"
          class="upload-btn" 
          @click="triggerImageUpload"
          title="上传图片"
        >
          🖼️
        </button>
        <div 
          v-if="(videoType === 'image-to-video' || videoType === 'digital-human') && uploadedImagePreview" 
          class="uploaded-preview"
        >
          <img :src="uploadedImagePreview" alt="预览" class="preview-img">
          <button class="remove-preview" @click="removeUploaded">×</button>
        </div>
        
        <input type="file" ref="audioUpload" accept="audio/*" style="display: none" @change="handleAudioUpload">
        <button 
          v-if="videoType === 'digital-human'"
          class="upload-btn" 
          @click="triggerAudioUpload"
          title="上传音频"
        >
          🎵
        </button>
        <div v-if="videoType === 'digital-human' && form.audioFileName" class="uploaded-filename">
          <span>{{ form.audioFileName }}</span>
          <button class="remove-filename" @click="removeAudio">×</button>
        </div>
      </div>
      
      <!-- 生成按钮 -->
      <button class="generate-btn" @click="generateVideo" :disabled="isGenerating || !isModelValid" :title="isModelValid ? '' : '当前模型不支持此功能，请选择其他模型'">
        <span v-if="!isGenerating">生成视频</span>
        <span v-else>生成中...</span>
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import appCache from '../../utils/appCache'

export default {
  name: 'VideoToolbar',
  props: {
    params: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      user_id: 0,
      videoType: 'text-to-video',
      isGenerating: false,
      isCollapsed: false,
      uploadedImage: null,
      uploadedAudio: null,
      uploadedImagePreview: null,
      pendingParams: null,
      hasAutoGenerated: false,
      isAutoSettingType: false,
      models: {},
      freeModels: [],
      paidModels: [],
      textToVideoModels: [],
      imageToVideoModels: [],
      avatarVideoModels: [],
      videoSizes: [],
      videoDurations: [],
      form: {
        prompt: '',
        model: '',
        size: '',
        duration: '5',
        referenceImage: null,
        audioFile: null,
        audioFileName: null
      }
    }
  },
  computed: {
    // 检查当前选择的模型是否支持当前videoType的功能
    isModelValid() {
      if (!this.form.model) return false
      const model = this.models[this.form.model]
      if (!model || !model.capabilities) return true
      
      if (this.videoType === 'text-to-video') {
        return model.capabilities.includes('text_to_video')
      } else if (this.videoType === 'image-to-video') {
        return model.capabilities.includes('image_to_video')
      } else if (this.videoType === 'digital-human') {
        return model.capabilities.includes('avatar_video')
      }
      return true
    }
  },
  watch: {
    params: {
      handler(newParams) {
        if (newParams && Object.keys(newParams).length > 0) {
          // 保存参数，等待初始化完成后处理
          this.pendingParams = newParams
        }
      },
      deep: true,
      immediate: true
    },
    // 切换标签时清空上传的图片和音频
    videoType() {
      // 如果是自动设置videoType（来自handleParams），不清空图片
      if (this.isAutoSettingType) {
        return
      }
      this.form.referenceImage = null
      this.uploadedImage = null
      this.uploadedAudio = null
      this.uploadedImagePreview = null
    }
  },
  async mounted() {
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
    this.user_id = userInfo.id || 0
    console.log('🚀 VideoToolbar 初始化开始')
    
    await Promise.all([
      this.loadModels(),
      this.loadVideoSizes(),
      this.loadVideoDurations()
    ])
    
    // 从后台配置获取默认模型、分辨率和时长
    try {
      const response = await axios.get('/admin/config/video')
      if (response.data.code === 200) {
        const config = response.data.data
        const defaultModel = config.model || ''
        const defaultVideoSize = config.defaultVideoSize || '1920x1080 (16:9)'
        const defaultDuration = config.defaultDuration || '15'
        
        // 设置默认模型
        if (defaultModel && this.models[defaultModel]) {
          this.form.model = defaultModel
        } else if (this.freeModels.length > 0) {
          this.form.model = this.freeModels[0]
        }
        
        // 设置默认分辨率
        if (defaultVideoSize) {
          const matchedSize = this.videoSizes.find(s => s.name === defaultVideoSize)
          if (matchedSize) {
            this.form.size = matchedSize.name
          } else if (this.videoSizes.length > 0 && !this.form.size) {
            this.form.size = this.videoSizes[0].name
          }
        }
        
        // 设置默认时长
        if (defaultDuration) {
          const matchedDuration = this.videoDurations.find(d => d.duration === defaultDuration)
          if (matchedDuration) {
            this.form.duration = matchedDuration.duration
          } else if (this.videoDurations.length > 0 && !this.form.duration) {
            this.form.duration = this.videoDurations[0].duration
          }
        }
      }
    } catch (error) {
      console.error('获取视频配置失败:', error)
    }
    
    console.log('🚀 VideoToolbar 初始化完成，videoSizes:', this.videoSizes)
    
    // 处理待处理的参数
    if (this.pendingParams) {
      console.log('初始化完成，开始处理待处理参数')
      this.handleParams(this.pendingParams)
      this.pendingParams = null
    }
  },
  methods: {
    
    async loadVideoSizes() {
      try {
        const sizes = await appCache.getVideoSizes()
        if (sizes) {
          this.videoSizes = sizes
          if (this.videoSizes.length > 0) {
            this.form.size = this.videoSizes[0].name
          }
        }
      } catch (error) {
        console.error('加载视频尺寸失败', error)
      }
    },

    async loadVideoDurations() {
      try {
        const response = await axios.get('/api/video-durations')
        if (response.data.code === 200) {
          this.videoDurations = response.data.video_durations
        }
      } catch (error) {
        console.error('加载视频时长失败', error)
      }
    },
    async loadModels() {
      try {
        // 加载所有视频模型
        const allVideoModels = await appCache.getModelsByCategory('video')
        // 加载支持文生视频的模型
        const textToVideoModels = await appCache.getModelsByCategory('video', false, 'text_to_video')
        // 加载支持图生视频的模型
        const imageToVideoModels = await appCache.getModelsByCategory('video', false, 'image_to_video')
        // 加载支持数字人的模型
        const avatarVideoModels = await appCache.getModelsByCategory('video', false, 'avatar_video')
        
        this.models = {}
        this.freeModels = []
        this.paidModels = []
        this.textToVideoModels = []
        this.imageToVideoModels = []
        this.avatarVideoModels = []
        
        allVideoModels.forEach(model => {
          this.models[model.identifier] = {
            name: model.name,
            description: model.description,
            type: model.model_type,
            image_url: model.image_url,
            is_new: model.is_new,
            is_hot: model.is_hot,
            discount: model.discount,
            capabilities: model.capabilities || []
          }
          if (model.model_type === 'free') {
            this.freeModels.push(model.identifier)
          } else {
            this.paidModels.push(model.identifier)
          }
        })
        
        // 按能力分类
        textToVideoModels.forEach(model => {
          this.textToVideoModels.push(model.identifier)
        })
        imageToVideoModels.forEach(model => {
          this.imageToVideoModels.push(model.identifier)
        })
        avatarVideoModels.forEach(model => {
          this.avatarVideoModels.push(model.identifier)
        })
        
        if (!this.form.model && this.freeModels.length > 0) {
          // 根据当前videoType选择默认模型
          let targetModels = []
          if (this.videoType === 'text-to-video') {
            targetModels = this.textToVideoModels
          } else if (this.videoType === 'image-to-video') {
            targetModels = this.imageToVideoModels
          } else if (this.videoType === 'digital-human') {
            targetModels = this.avatarVideoModels
          }
          
          const defaultModel = targetModels.find(id => this.freeModels.includes(id)) || targetModels[0]
          if (defaultModel) {
            this.form.model = defaultModel
          }
        }
      } catch (error) {
        console.error('加载模型列表失败', error)
      }
    },

    async handleParams(params) {
      console.log('=== VideoToolbar 处理参数 ===', params)
      
      // 先填充表单字段（在设置videoType之前，避免watch清空图片）
      if (params.prompt) {
        this.form.prompt = params.prompt
      }
      if (params.model) {
        this.form.model = params.model
      }
      if (params.duration) {
        this.form.duration = params.duration
      }
      if (params.size) {
        // 根据参数中的size找到对应的视频尺寸对象
        const matchedSize = this.videoSizes.find(s => s.name === params.size || s.aspect_ratio === params.size)
        if (matchedSize) {
          this.form.size = matchedSize.name
        } else {
          // 如果没匹配到，直接使用参数值
          this.form.size = params.size
        }
      }
      if (params.hasReferenceImage === '1') {
        const refImage = sessionStorage.getItem('videoReferenceImage')
        if (refImage) {
          this.form.referenceImage = refImage
          this.uploadedImagePreview = refImage
        }
      }
      if (params.hasAvatarImage === '1') {
        const avatarImage = sessionStorage.getItem('videoAvatarImage')
        if (avatarImage) {
          this.form.referenceImage = avatarImage
          this.uploadedImagePreview = avatarImage
        }
      }
      if (params.hasAudioFile === '1') {
        const audioBase64 = sessionStorage.getItem('videoAudioFile')
        const audioFileName = sessionStorage.getItem('videoAudioFileName') || 'audio.mp3'
        if (audioBase64) {
          // 将 base64 转换回 File 对象
          this.uploadedAudio = this.dataURLtoFile(audioBase64, audioFileName)
          console.log('音频文件已恢复:', audioFileName)
        }
      }
      
      // 最后设置videoType（避免watch清空刚设置的图片）
      if (params.type) {
        this.isAutoSettingType = true
        if (params.type === 'text') {
          this.videoType = 'text-to-video'
        } else if (params.type === 'image') {
          this.videoType = 'image-to-video'
        } else if (params.type === 'avatar') {
          this.videoType = 'digital-human'
        }
        // 重置标志，允许用户后续手动切换
        this.$nextTick(() => {
          this.isAutoSettingType = false
        })
      }
      
      console.log('参数填充完成，表单状态:', this.form)
      
      // 等待模型和尺寸加载完成后自动生成
      await this.waitForLoadComplete()
      
      // 避免重复生成
      if (!this.hasAutoGenerated) {
        this.hasAutoGenerated = true
        // 延迟一下，确保所有数据都准备好了
        setTimeout(() => {
          this.generateVideo()
        }, 500)
      }
    },
    
    async waitForLoadComplete() {
      // 等待模型、尺寸和时长加载完成
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (this.videoSizes.length > 0 && this.videoDurations.length > 0 && Object.keys(this.models).length > 0) {
            clearInterval(checkInterval)
            resolve()
          }
        }, 100)
        // 最多等待3秒
        setTimeout(() => {
          clearInterval(checkInterval)
          resolve()
        }, 3000)
      })
    },

    getPromptPlaceholder() {
      switch(this.videoType) {
        case 'text-to-video':
          return '描述您想要生成的视频场景...'
        case 'image-to-video':
          return '描述您想要生成的视频动作或场景...'
        case 'digital-human':
          return '描述数字人的动作、表情或场景...'
        default:
          return '描述您想要生成的视频...'
      }
    },
    async generateVideo() {
      // 检查模型能力
      if (!this.isModelValid) {
        let capabilityName = ''
        if (this.videoType === 'text-to-video') capabilityName = '文生视频'
        else if (this.videoType === 'image-to-video') capabilityName = '图生视频'
        else if (this.videoType === 'digital-human') capabilityName = '数字人'
        alert(`当前模型不支持${capabilityName}功能，请选择其他模型`)
        return
      }
      
      if (!this.form.prompt.trim()) {
        console.warn('请输入提示词')
        return
      }

      // 图生视频需要上传参考图片
      if (this.videoType === 'image-to-video' && !this.form.referenceImage) {
        console.warn('请先上传参考图片')
        return
      }

      // 数字人需要上传头像图片和音频文件
      if (this.videoType === 'digital-human') {
        if (!this.form.referenceImage) {
          console.warn('请先上传人物角色参考图片')
          return
        }
        if (!this.uploadedAudio) {
          console.warn('请先上传音频文件')
          return
        }
      }

      // 先创建一个generating状态的视频项，立即显示在列表
      this.isGenerating = true
      
      // 平滑滚动到顶部，让用户看到生成过程
      window.scrollTo({ top: 0, behavior: 'smooth' })
      
      const newVideo = {
        type: this.videoType,
        prompt: this.form.prompt,
        model: this.form.model,
        size: this.form.size,
        duration: this.form.duration,
        uploadedImage: (this.videoType === 'image-to-video' || this.videoType === 'digital-human') ? this.form.referenceImage : null,
        imageAspect: this.form.size,
        thumbnail: '',
        videoUrl: '',
        status: 'generating',
        progress: 0,
        progressText: '正在生成...',
        generateTime: this.getCurrentTime()
      }
      
      // 先emit添加到列表
      this.$emit('add-video', newVideo)
      
      this.isGenerating = true
      
      // 添加简单的进度模拟，每3秒增加约5%
      const progressInterval = setInterval(() => {
        newVideo.progress = Math.min((newVideo.progress || 0) + 5, 95)
        this.$emit('update-video-progress', { index: 0, progress: newVideo.progress })
      }, 3000)
      
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
        const userId = userInfo.id || 0
        
        let mergedPrompt = this.form.prompt
        
        if (this.form.size) {
          mergedPrompt += `，尺寸：${this.form.size}`
        }
        
        if (this.form.duration) {
          const durOption = this.videoDurations.find(d => d.duration === this.form.duration)
          mergedPrompt += `，时长：${durOption?.name || this.form.duration}秒`
        }
        
        if (this.videoType === 'digital-human') {
          mergedPrompt += '，数字人视频'
        } else if (this.videoType === 'image-to-video') {
          mergedPrompt += '，图生视频'
        } else {
          mergedPrompt += '，文生视频'
        }
        
        // 声明 response 在 try 块顶部
        let response = null
        
        if (this.videoType === 'digital-human') {
          // 数字人 - 先上传头像到存储
          let avatarUrl = null
          if (this.uploadedImagePreview && (this.uploadedImagePreview.startsWith('/') || this.uploadedImagePreview.startsWith('http'))) {
            avatarUrl = this.uploadedImagePreview
          } else if (this.uploadedImage) {
            console.log('数字人：上传头像图片到存储...')
            const uploadForm = new FormData()
            uploadForm.append('image', this.uploadedImage, this.uploadedFileName || 'avatar.jpg')
            uploadForm.append('user_id', userId)
            const uploadResp = await axios.post('/upload-image', uploadForm, {
              headers: { 'Content-Type': 'multipart/form-data' }
            })
            avatarUrl = uploadResp.data.url
            console.log('头像上传成功:', avatarUrl)
          }
          
          // 数字人API
          const formData = new FormData()
          formData.append('model', this.form.model)
          formData.append('size', this.form.size)
          formData.append('avatarImage', avatarUrl || this.form.referenceImage)
          formData.append('audioFile', this.uploadedAudio)
          formData.append('detailPrompt', mergedPrompt)
          formData.append('user_id', userId)

          response = await axios.post('/avatar-video', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
          if (response.data.failed) {
            throw new Error(response.data.message || '生成失败')
          }
        } else if (this.videoType === 'image-to-video') {
          // 图生视频 - 先上传图片到存储
          let referenceUrl = null
          if (this.uploadedImagePreview && (this.uploadedImagePreview.startsWith('/') || this.uploadedImagePreview.startsWith('http'))) {
            referenceUrl = this.uploadedImagePreview
          } else if (this.uploadedImage) {
            console.log('图生视频：上传参考图片到存储...')
            const uploadForm = new FormData()
            uploadForm.append('image', this.uploadedImage, this.uploadedFileName || 'reference.jpg')
            uploadForm.append('user_id', userId)
            const uploadResp = await axios.post('/upload-image', uploadForm, {
              headers: { 'Content-Type': 'multipart/form-data' }
            })
            referenceUrl = uploadResp.data.url
            console.log('参考图片上传成功:', referenceUrl)
          }
          
          const formData = new FormData()
          formData.append('reference_image_url', referenceUrl)
          formData.append('prompt', mergedPrompt)
          formData.append('model', this.form.model)
          formData.append('size', this.form.size)
          formData.append('duration', this.form.duration)
          formData.append('user_id', userId)

          response = await axios.post('/video-generate-upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
          if (response.data.failed) {
            throw new Error(response.data.message || '生成失败')
          }
          // 发送事件通知父组件刷新视频历史
          this.$emit('video-generated', {
            videoUrl: response.data.video_url,
            thumbnail: response.data.thumbnail,
            prompt: mergedPrompt,
            model: this.form.model,
            duration: this.form.duration,
            size: this.form.size
          })
        } else {
          // 文生视频API
          response = await axios.post('/video-generate', {
            prompt: mergedPrompt,
            model: this.form.model,
            duration: this.form.duration,
            size: this.form.size,
            user_id: userId
          })
          if (response.data.failed) {
            throw new Error(response.data.message || '生成失败')
          }
          // 发送事件通知父组件刷新视频历史
          this.$emit('video-generated', {
            videoUrl: response.data.video_url,
            thumbnail: response.data.thumbnail,
            prompt: mergedPrompt,
            model: this.form.model,
            duration: this.form.duration,
            size: this.form.size
          })
        }
        
        console.log('视频生成成功:', response.data)
        
        // 停止进度模拟
        clearInterval(progressInterval)
        // 通知父组件更新结果
        this.$emit('video-generated', {
          videoUrl: response.data.video_url,
          thumbnail: response.data.thumbnail,
          prompt: mergedPrompt,
          model: this.form.model,
          duration: this.form.duration,
          size: this.form.size
        })
      } catch (error) {
        console.error('生成失败:', error)
        
        // 停止进度模拟
        clearInterval(progressInterval)
        // 设置进度为100，显示失败提示
        newVideo.progress = 100
        this.$emit('update-video-progress', { index: 0, progress: 100 })
      } finally {
        this.isGenerating = false
        
        // 重置表单
        this.form.prompt = ''
        this.uploadedImage = null
        this.form.referenceImage = null
      }
    },
    dataURLtoFile(dataurl, filename) {
      const arr = dataurl.split(',')
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      return new File([u8arr], filename, { type: mime })
    },
    
    getCurrentTime() {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    },
    triggerImageUpload() {
      if (this.$refs.imageUpload) {
        this.$refs.imageUpload.click()
      }
    },
    handleImageUpload(event) {
      const file = event.target.files[0]
      if (file) {
        console.log('上传图片:', file.name)
        this.uploadedImage = file
        this.uploadedImagePreview = null
        const reader = new FileReader()
        reader.onload = (e) => {
          this.form.referenceImage = e.target.result
          this.uploadedImagePreview = e.target.result
          console.log('图片上传成功')
        }
        reader.readAsDataURL(file)
      }
    },
    removeUploaded() {
      this.uploadedImage = null
      this.uploadedImagePreview = null
      this.form.referenceImage = null
      if (this.$refs.imageUpload) {
        this.$refs.imageUpload.value = ''
      }
    },
    removeAudio() {
      this.uploadedAudio = null
      this.form.audioFile = null
      this.form.audioFileName = null
      if (this.$refs.audioUpload) {
        this.$refs.audioUpload.value = ''
      }
    },
    triggerAudioUpload() {
      if (this.$refs.audioUpload) {
        this.$refs.audioUpload.click()
      }
    },
    handleAudioUpload(event) {
      const file = event.target.files[0]
      if (file) {
        console.log('上传音频:', file.name)
        this.uploadedAudio = file
        this.form.audioFile = file
        this.form.audioFileName = file.name
        console.log('音频上传成功')
      }
    },
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed
    }
  }
}
</script>

<style scoped>
.toolbar-wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
  transition: all 0.3s ease;
}

/* 工具栏头部 */
.toolbar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 60px;
  position: relative;
}

/* 顶部按钮栏 */
.toolbar-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

/* 收缩/展开按钮 */
.collapse-btn {
  background: transparent;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #4ade80;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
}

.collapse-btn.expanded {
  color: #ff4757;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
}

.collapse-btn:hover {
  transform: translate(-50%, -50%) scale(1.1);
}

.collapse-btn.expanded:hover {
  transform: translate(-50%, -50%) scale(1.1);
}

.collapse-arrow {
  transition: transform 0.3s ease, color 0.3s ease;
  font-size: 20px;
  font-weight: bold;
}

.collapse-arrow.up {
  transform: rotate(180deg);
}

/* 收缩状态 */
.toolbar-wrapper.collapsed {
  height: 60px;
  overflow: visible;
}

.toolbar-wrapper.collapsed .prompt-input-container,
.toolbar-wrapper.collapsed .toolbar-options {
  display: none;
}

.toolbar-wrapper.collapsed .toolbar-header {
  align-items: center;
  justify-content: center;
}

/* 底部展开按钮 */
.expand-btn {
  position: fixed;
  bottom: 20px;
  right: 50%;
  transform: translateX(50%);
  background: #ff4757;
  border: none;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(255, 71, 87, 0.4);
  z-index: 1000;
}

.expand-btn:hover {
  transform: translateX(50%) scale(1.1);
  box-shadow: 0 6px 16px rgba(255, 71, 87, 0.6);
}

.expand-arrow {
  color: white;
  font-size: 24px;
  font-weight: bold;
  transition: transform 0.3s ease;
}

.toolbar-btn {
  background: rgba(255, 255, 255, 0.1);
  color: #e6f1ff;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.toolbar-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.toolbar-btn.active {
  background: #00e6ac;
  color: #000;
  border-color: #00e6ac;
  font-weight: 600;
}

/* 提示词输入区 */
.prompt-input-container {
  width: 100%;
}

.prompt-input {
  width: 100%;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px;
  color: #e6f1ff;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  min-height: 80px;
}

.prompt-input::placeholder {
  color: #8892b0;
}

/* 底部参数选项 */
.toolbar-options {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
}

.option-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.option-select {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: 8px 12px;
  color: #e6f1ff;
  font-size: 14px;
  min-width: 100px;
  cursor: pointer;
}

.option-select option {
  background: #2a2a2a;
  color: #e6e6e6;
}

.option-select optgroup {
  background: #1a1a1a;
  color: #00e6ac;
  font-weight: 600;
}

/* 生成按钮 */
.generate-btn {
  background: #00e6ac;
  color: #000;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-left: auto;
  white-space: nowrap;
}

.generate-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 230, 172, 0.3);
}

.generate-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* 上传按钮 */
.upload-buttons {
  display: flex;
  gap: 10px;
  align-items: center;
}

.upload-btn {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #e6f1ff;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
  border-color: #64ffda;
  box-shadow: 0 2px 8px rgba(100, 255, 218, 0.3);
}

.uploaded-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
}

.preview-img {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid rgba(0, 230, 172, 0.3);
  background: rgba(255, 255, 255, 0.02);
}

.remove-preview {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(255, 71, 87, 0.9);
  color: white;
  border: none;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  line-height: 1;
  padding: 0;
}

.remove-preview:hover {
  background: rgba(255, 71, 87, 1);
  transform: scale(1.1);
}

.uploaded-filename {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 6px;
}

.uploaded-filename span {
  color: #fff;
  font-size: 13px;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.remove-filename {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(255, 107, 107, 0.2);
  border: 1px solid rgba(255, 107, 107, 0.4);
  color: #ff6b6b;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.remove-filename:hover {
  background: rgba(255, 107, 107, 0.3);
}

@media (max-width: 768px) {
  .toolbar-options {
    flex-direction: column;
    align-items: stretch;
  }
  
  .generate-btn {
    margin-left: 0;
  }
}
</style>